import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";

/**
 * MK2 (GO Frank 3 november 2026): de modelkeuze van DIT gesprek.
 *
 * Besluit Frank: de keuze geldt per gesprek, met "Standaard uit Instellingen" als terugweg (beide leeg). In een gesprek
 * komt de stand van de server (`/conversations/{id}`) en gaat een keuze daar ook heen. Op het welkomstscherm bestaat er
 * nog geen gesprek: dan staat de keuze hier als WACHTEND, en `pasToe` schrijft haar weg zodra het eerste bericht het
 * gesprek heeft aangemaakt, VOOR het bericht zelf, zodat al het eerste antwoord met dit model komt. Zelfde vorm als
 * `gesprekConnectors.js`.
 */

export const STANDAARD = Object.freeze({ aanbieder: null, model: null, denkniveau: null });

/** De keuze uit een gesprek zoals de server het geeft, met het denkniveau (MK4). PUUR. */
export function keuzeVan(gesprek) {
  return { aanbieder: gesprek?.keuze_aanbieder || null, model: gesprek?.keuze_model || null,
           denkniveau: gesprek?.keuze_denkniveau || null };
}

/** MK6 (3 nov): geheugen en onderzoek op het web in dit gesprek; standaard allebei aan (besluit Frank). */
export const OPTIES_STANDAARD = Object.freeze({ geheugen: true, onderzoek: true });

/** De opties uit een gesprek zoals de server het geeft. PUUR. */
export function optiesVan(gesprek) {
  return { geheugen: !gesprek?.geheugen_uit, onderzoek: !gesprek?.web_uit };
}

export function useGesprekModel(conversationId) {
  const [keuze, setKeuze] = useState(STANDAARD);
  const wachtend = useRef(null);
  const [opties, setOpties] = useState(OPTIES_STANDAARD);
  const optiesWachtend = useRef(null);

  useEffect(() => {
    if (!conversationId) {
      setKeuze(wachtend.current || STANDAARD);
      setOpties(optiesWachtend.current || OPTIES_STANDAARD);
      return;
    }
    let weg = false;
    axios.get(`${API}/conversations/${conversationId}`)
      .then((r) => { if (!weg) { setKeuze(keuzeVan(r.data)); setOpties(optiesVan(r.data)); } })
      .catch(() => { if (!weg) { setKeuze(STANDAARD); setOpties(OPTIES_STANDAARD); } });
    return () => { weg = true; };
  }, [conversationId]);

  /** Een model kiezen; `aanbieder` leeg is "Standaard uit Instellingen". Een weigering van de server (geen sleutel)
   *  gaat als fout naar de aanroeper, en de keuze blijft dan wat ze was. */
  const kies = useCallback(async (aanbieder, model, denkniveau = null) => {
    // MK4: een ander model begint op het standaard denkniveau, tenzij er een niveau bij wordt gegeven.
    const nieuw = aanbieder || denkniveau ? { aanbieder: aanbieder || null, model: aanbieder ? model || null : null,
                                              denkniveau: denkniveau || null } : STANDAARD;
    if (conversationId) {
      const r = await axios.put(`${API}/conversations/${conversationId}/model`, nieuw);
      setKeuze(keuzeVan(r.data));
      return;
    }
    wachtend.current = aanbieder || denkniveau ? nieuw : null;
    setKeuze(nieuw);
  }, [conversationId]);

  /** MK4: alleen het denkniveau van dit gesprek verzetten; het model blijft wat het was. Leeg is de standaard. */
  const kiesNiveau = useCallback((niveau) => kies(keuze.aanbieder, keuze.model, niveau || null), [kies, keuze]);

  /** MK6: geheugen of onderzoek aan of uit in dit gesprek (naam "geheugen" of "onderzoek"). */
  const zetOptie = useCallback(async (naam, aan) => {
    if (conversationId) {
      const r = await axios.put(`${API}/conversations/${conversationId}/plus`, { [naam]: aan });
      setOpties(optiesVan(r.data));
      return;
    }
    const nieuw = { ...(optiesWachtend.current || OPTIES_STANDAARD), [naam]: aan };
    optiesWachtend.current = nieuw;
    setOpties(nieuw);
  }, [conversationId]);

  /** Na het aanmaken van een gesprek, voor het eerste bericht: de keuze van het welkomstscherm erin. */
  const pasToe = useCallback(async (convId) => {
    const o = optiesWachtend.current;
    optiesWachtend.current = null;
    if (o && (o.geheugen !== OPTIES_STANDAARD.geheugen || o.onderzoek !== OPTIES_STANDAARD.onderzoek)) {
      try {
        await axios.put(`${API}/conversations/${convId}/plus`, o);
      } catch {
        // Lukt dit niet, dan antwoordt het gesprek met de standaard (aan); het menu toont na herladen de stand.
      }
    }
    const w = wachtend.current;
    wachtend.current = null;
    if (!w) return;
    try {
      await axios.put(`${API}/conversations/${convId}/model`, w);
    } catch {
      // Een sleutel die intussen weg is: het gesprek antwoordt met de standaard, zoals de server ook doet.
    }
  }, []);

  return { keuze, kies, kiesNiveau, pasToe, opties, zetOptie };
}
